/** @jsx jsx */
import { jsx } from '@emotion/core'
import { ProcedimentoOdontoSelectModel } from 'components/form/field/select/ProcedimentoOdontoSelectField'
import { resolveName, UseFieldProps } from 'components/form/final-form/useField'
import { ParteBucalEnum } from 'graphql/types.generated'
import { Fragment, useState } from 'react'
import { Field, FieldRenderProps } from 'react-final-form'
import { MetaPath } from 'util/metaPath'

import { Sextantes } from '../../odontograma/Sextantes'
import { convertSextantesFieldModelToSextantesModel } from '../converter'
import { OdontogramaFieldModel } from '../OdontogramaField'
import SextanteFormPopper, { SextanteFormModel } from './SextanteFormPopper'

export interface SextanteFieldModel {
  observacoes: string[]
  procedimentos?: ProcedimentoOdontoSelectModel[]
}

export interface SextantesFieldProps extends UseFieldProps {
  name: MetaPath<Record<ParteBucalEnum, SextanteFieldModel>>
  cidadaoDataNascimento: LocalDate
  proteseTotalSuperior?: boolean
  proteseTotalInferior?: boolean
  initialValue?: OdontogramaFieldModel['sextantes']
}

interface SextanteActive {
  parteBucalEnum: ParteBucalEnum
  ref: HTMLButtonElement
}

export default function SextantesField(props: SextantesFieldProps) {
  const { name, proteseTotalSuperior, proteseTotalInferior, cidadaoDataNascimento, initialValue } = props
  const [popperOpen, setPopperOpen] = useState(false)
  const [activeSextante, setActiveSextante] = useState<SextanteActive>()

  const onClick = (sextante: ParteBucalEnum, ref: HTMLButtonElement) => {
    setActiveSextante({ parteBucalEnum: sextante, ref })
    setPopperOpen(true)
  }

  const renderField = (fieldProps: FieldRenderProps<OdontogramaFieldModel['sextantes'], any>) => {
    const {
      input: { value, onChange },
    } = fieldProps

    const fieldValues = Object.entries(value) as [ParteBucalEnum, SextanteFieldModel][]

    const handleChange = (newValues: [ParteBucalEnum, SextanteFieldModel][]) => {
      const o = Object.fromEntries(new Map([...fieldValues, ...newValues] as [ParteBucalEnum, SextanteFieldModel][]))
      onChange(o)
    }

    const handleSubmit = (formValues: SextanteFormModel) => {
      const { replicarItems, ...values } = formValues

      setPopperOpen(false)

      values &&
        handleChange([
          ...((replicarItems &&
            replicarItems.map(
              (replicarItem) => [replicarItem.value, values] as [ParteBucalEnum, SextanteFieldModel]
            )) ||
            []),
          [activeSextante.parteBucalEnum, values],
        ])

      setActiveSextante(undefined)
    }

    return (
      <Fragment>
        <Sextantes
          onClick={onClick}
          activeItem={activeSextante?.parteBucalEnum}
          proteseTotalSuperior={proteseTotalSuperior}
          proteseTotalInferior={proteseTotalInferior}
          value={convertSextantesFieldModelToSextantesModel(value)}
        />

        {activeSextante && (
          <SextanteFormPopper
            parteBucalEnum={activeSextante?.parteBucalEnum}
            cidadaoDataNascimento={cidadaoDataNascimento}
            open={popperOpen}
            placement='top'
            anchorRef={activeSextante.ref}
            initialValues={value[activeSextante.parteBucalEnum]}
            sextantesValues={value}
            onSubmit={handleSubmit}
          />
        )}
      </Fragment>
    )
  }

  return (
    <Field name={resolveName(name)} render={renderField} initialValue={initialValue} subscription={{ value: true }} />
  )
}
